import { ComboBox } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { isValidEmail } from '../../../../../../../../../../utils/isValidEmail'
import { SendAsEmailModalVariant } from '../../../../../../../../enums/sendAsEmailModalVariant'
import { useSendAsEmailForm } from '../../../../../../contexts/sendAsEmailFormContext'
import { useContactPersonItems } from '../../../../../../hooks/useContactPersonItems'
import { useTrackSendAsEmail } from '../../../../../../hooks/useTrackSendAsEmail'

export const EmailRecipients = (): ReactElement => {
  const { t } = useTranslation()
  const { contactPersonItems } = useContactPersonItems()
  const { sendAsEmailForm, variant } = useSendAsEmailForm()
  const { trackSendAsEmail } = useTrackSendAsEmail()
  const { FormItem } = sendAsEmailForm

  const isSendInvoiceAsEmailModal = variant === SendAsEmailModalVariant.Invoice

  const handleEmailRecipientsChange = useCallback(
    (emails: string[]) => {
      // We take last email from ComboBox onSelect event to track only selected email.
      const email = emails[emails.length - 1]
      trackSendAsEmail('Email selected from dropdown', { email })
    },
    [trackSendAsEmail],
  )

  const handleDebouncedEmailInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      isSendInvoiceAsEmailModal && trackSendAsEmail('Email typed', { email: event.target.value })
    },
    [isSendInvoiceAsEmailModal, trackSendAsEmail],
  )

  return (
    <FormItem
      name="emailRecipients"
      render={({ field: { onChange, value, ...rest } }) => {
        return (
          <ComboBox
            {...rest}
            addNewItemButtonText={t('external_invoices.send_as_email.modal.form.field.recipients.add_item')}
            isMultiselect
            items={contactPersonItems}
            maxItems={3}
            onInputChangeDebounced={handleDebouncedEmailInputChange}
            onChange={(event) => {
              onChange(event)
              handleEmailRecipientsChange(event)
            }}
            placeholder={t('external_invoices.send_as_email.modal.form.field.recipients.placeholder')}
            selectedItemsPrefix={t('external_invoices.send_as_email.modal.form.field.recipients.label')}
            selectedValues={value}
            size="sm"
            validateItem={isValidEmail}
          />
        )
      }}
    />
  )
}
